import { create } from 'zustand';
import axios from '../config/axios';
import { API_BASE_URL } from '../config/constants';

// Função para normalizar a URL do avatar
const getAvatarUrl = (avatarPath) => {
  if (!avatarPath) return null;
  if (avatarPath.startsWith('http') || avatarPath.startsWith('data:')) {
    return avatarPath;
  }
  // Remove o prefixo /api/ para usar o servidor de arquivos estáticos diretamente
  return `${API_BASE_URL}/${avatarPath}`;
};

// Função para recuperar dados iniciais do localStorage
const getInitialState = () => {
  const token = localStorage.getItem('token');
  const userData = localStorage.getItem('userData');
  const user = userData ? JSON.parse(userData) : null;
  
  if (token) {
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  }

  // Garante que a URL do avatar esteja normalizada
  if (user?.avatarUrl) {
    user.avatarUrl = getAvatarUrl(user.avatarUrl);
  }

  return {
    token,
    user,
    isAuthenticated: !!token && !!user,
    isLoading: true // Começa como true para evitar flash de conteúdo não autenticado
  };
};

// Função para salvar dados do usuário
const saveUserData = (userData) => {
  if (userData) {
    // Normaliza a URL do avatar antes de salvar
    const normalizedData = {
      ...userData,
      avatarUrl: userData.avatarUrl ? getAvatarUrl(userData.avatarUrl) : null,
      // Garantindo que os campos essenciais existam
      id: userData.id,
      email: userData.email,
      role: userData.role,
      tradeName: userData.tradeName || '',
      cnpj: userData.cnpj || '',
      profile: {
        ...userData.profile,
        logradouro: userData.profile?.logradouro || '',
        numero: userData.profile?.numero || '',
        complemento: userData.profile?.complemento || '',
        bairro: userData.profile?.bairro || '',
        cidade: userData.profile?.cidade || '',
        estado: userData.profile?.estado || '',
        cep: userData.profile?.cep || '',
        telefone: userData.profile?.telefone || ''
      }
    };
    localStorage.setItem('userData', JSON.stringify(normalizedData));
  } else {
    localStorage.removeItem('userData');
  }
};

const useAuthStore = create((set, get) => ({
  ...getInitialState(),

  setUser: (userData) => {
    // Normaliza a URL do avatar antes de atualizar o estado
    const normalizedData = userData ? {
      ...userData,
      avatarUrl: getAvatarUrl(userData.avatarUrl)
    } : null;
    
    saveUserData(normalizedData);
    set({ 
      user: normalizedData,
      isAuthenticated: !!normalizedData
    });
  },

  setLoading: (isLoading) => set({ isLoading }),

  register: async (userData) => {
    try {
      const response = await axios.post('/auth/register', userData);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  login: async (credentials) => {
    try {
      set({ isLoading: true });
      const response = await axios.post('/auth/login', credentials);

      const { access_token, user } = response.data;

      if (!access_token || !user) {
        throw new Error('Dados de autenticação inválidos');
      }

      // Configura o token e dados do usuário
      localStorage.setItem('token', access_token);
      saveUserData(user);
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;

      // Atualiza o estado
      set({ 
        token: access_token,
        user: {
          ...user,
          avatarUrl: getAvatarUrl(user.avatarUrl)
        },
        isAuthenticated: true,
        isLoading: false
      });

      // Retorna os dados necessários para o redirecionamento
      return {
        user: {
          ...user,
          avatarUrl: getAvatarUrl(user.avatarUrl)
        }
      };
    } catch (error) {
      // Limpa dados de autenticação
      localStorage.removeItem('token');
      localStorage.removeItem('userData');
      delete axios.defaults.headers.common['Authorization'];
      
      set({ 
        token: null,
        user: null,
        isAuthenticated: false,
        isLoading: false
      });
      
      throw error.response?.data || error;
    }
  },

  logout: () => {
    // Limpa localStorage
    localStorage.removeItem('token');
    localStorage.removeItem('userData');
    
    // Limpa headers do axios
    delete axios.defaults.headers.common['Authorization'];
    
    // Reseta o estado
    set({
      token: null,
      user: null,
      isAuthenticated: false,
      isLoading: false
    });
  },

  checkAuth: async () => {
    const { setUser } = get();
    try {
      const token = localStorage.getItem('token');
      
      if (!token) {
        set({ 
          token: null,
          user: null,
          isAuthenticated: false,
          isLoading: false 
        });
        return false;
      }

      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;

      const response = await axios.get('/auth/profile');

      // Mapeia os campos do backend para o frontend
      const userData = {
        ...response.data,
        tradeName: response.data.tradeName || response.data.email,
        cnpj: response.data.cnpj || '',
        role: response.data.role || 'USER'
      };

      // Atualiza o estado com os dados do perfil
      setUser(userData);

      set({ 
        token,
        isAuthenticated: true,
        isLoading: false
      });

      return true;
    } catch (error) {
      // Limpa dados de autenticação em caso de erro
      localStorage.removeItem('token');
      localStorage.removeItem('userData');
      delete axios.defaults.headers.common['Authorization'];
      
      set({
        token: null,
        user: null,
        isAuthenticated: false,
        isLoading: false
      });
      
      return false;
    }
  }

}));

export default useAuthStore;
